import React, { useState, useEffect, useCallback } from 'react'
import {
  loadFieldForms, loadFieldForm, saveFieldForm, submitFieldForm,
  approveFieldForm, updateFormStatus, loadCostCodesForJob, exportPayrollCSV, loadHoursForWeek,
  sendFormNotification
} from '../lib/forms'
import { loadAllUsers } from '../lib/db'

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmtDate(d) {
  // Parse YYYY-MM-DD without timezone shift
  const parts = d.split('-')
  const dateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
  return dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

function StatusPill({ status }) {
  const styles = {
    draft:     { bg: '#F5F5F5', color: '#666' },
    submitted: { bg: '#FAEEDA', color: '#854F0B' },
    reviewed:  { bg: '#E6F1FB', color: '#0C447C' },
    approved:  { bg: '#EAF3DE', color: '#3B6D11' },
  }
  const s = styles[status] || styles.draft
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
      {status}
    </span>
  )
}

// ── Weather fetcher ────────────────────────────────────────────────────────────
async function fetchWeather(address) {
  try {
    const encoded = encodeURIComponent(address)
    const geoRes = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encoded}&count=1&language=en&format=json`)
    const geoData = await geoRes.json()
    if (!geoData.results?.length) return null
    const { latitude, longitude } = geoData.results[0]
    const wxRes = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,precipitation,windspeed_10m,relativehumidity_2m,weathercode&temperature_unit=fahrenheit&windspeed_unit=mph&precipitation_unit=inch`)
    const wx = await wxRes.json()
    const c = wx.current
    const desc = weatherCodeDesc(c.weathercode)
    return {
      temp: Math.round(c.temperature_2m),
      desc,
      precip: c.precipitation + '"',
      humidity: c.relativehumidity_2m + '%',
      wind: Math.round(c.windspeed_10m) + ' mph',
    }
  } catch (e) {
    console.error('Weather fetch error:', e)
    return null
  }
}

function weatherCodeDesc(code) {
  if (code === 0) return 'Clear skies'
  if (code <= 3) return 'Partly cloudy'
  if (code <= 48) return 'Foggy'
  if (code <= 67) return 'Rainy'
  if (code <= 77) return 'Snowy'
  if (code <= 82) return 'Rain showers'
  if (code <= 99) return 'Thunderstorms'
  return 'Mixed conditions'
}

// ── Form editor ────────────────────────────────────────────────────────────────
function FormEditor({ form, costCodes, roster, activeJobs, onSave, onSubmit, onClose, canEdit, cellData, userName }) {
  const [data, setData] = useState({
    work_description: form.work_description || '',
    delays: form.delays || '',
    non_contract_work: form.non_contract_work === null || form.non_contract_work === undefined ? null : form.non_contract_work,
    non_contract_details: form.non_contract_details || '',
    safety_issue: form.safety_issue === null || form.safety_issue === undefined ? null : form.safety_issue,
    safety_details: form.safety_details || '',
    delivery_no_receipt: form.delivery_no_receipt === null || form.delivery_no_receipt === undefined ? null : form.delivery_no_receipt,
    equipment_issues: form.equipment_issues || '',
    tomorrow_jobsite: form.tomorrow_jobsite || '',
    tomorrow_men: form.tomorrow_men || 0,
    tomorrow_tasks: form.tomorrow_tasks || '',
    tomorrow_materials: form.tomorrow_materials || '',
    concrete_masonry_placed: form.concrete_masonry_placed || false,
  })
  // Pre-fill workers from board cellData if form is draft and no entries yet
  const getInitialEntries = () => {
    if (form.entries && form.entries.length > 0) return form.entries
    if (form.status !== 'draft' || !cellData) return []
    // Board uses toDateString() format e.g. "Mon May 04 2026"
    // Form stores date as YYYY-MM-DD e.g. "2026-05-04"
    // Parse without timezone shift using local parts
    const parts = form.date ? form.date.split('-') : null
    const formDateObj = parts ? new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])) : null
    const boardDateKey = formDateObj ? formDateObj.toDateString() : ''
    const cell = cellData[form.job_id]?.[boardDateKey] || null
    console.log('Pre-fill lookup:', { job_id: form.job_id, boardDateKey, date: form.date, cell: cell ? 'found' : 'not found', workers: cell?.workers })
    if (!cell) return []
    // Build worker list — foreman first, then the rest
    const foremanName = cell.foreman || ''
    const workerNames = cell.workers || []
    // Combine foreman + workers, deduplicate
    const allNames = foremanName
      ? [foremanName, ...workerNames.filter(w => w !== foremanName)]
      : workerNames
    if (allNames.length === 0) return []
    return allNames.map(wname => {
      const emp = roster.find(e => e.name === wname) || {}
      return { employee_id: emp.id || null, employee_name: wname, hours: '', cost_code_no: '', cost_code_desc: '', notes: '' }
    })
  }
  const [entries, setEntries] = useState(getInitialEntries)
  const [concrete, setConcrete] = useState(form.concrete?.length ? form.concrete : [{ cost_code_no: '', cost_code_desc: '', quantity: '', notes: '' }])
  const [masonry, setMasonry] = useState(form.masonry?.length ? form.masonry : [])
  const [saving, setSaving] = useState(false)
  const [weather, setWeather] = useState({ temp: form.weather_temp, desc: form.weather_desc, precip: form.weather_precip, humidity: form.weather_humidity, wind: form.weather_wind })

  // Fetch weather if not set - try job address, fall back to city/state
  useEffect(() => {
    if (!form.weather_temp) {
      const addr = form.job_address || form.job_name || ''
      if (addr) {
        fetchWeather(addr).then(wx => {
          if (wx) setWeather(wx)
          else {
            // Try just the city part if full address fails
            const cityState = addr.split(',').slice(-2).join(',').trim()
            if (cityState && cityState !== addr) {
              fetchWeather(cityState).then(wx2 => { if (wx2) setWeather(wx2) })
            }
          }
        })
      }
    }
  }, [form.job_address, form.weather_temp, form.job_name])

  const set = (k, v) => setData(d => ({ ...d, [k]: v }))

  const addWorker = () => {
    setEntries(e => [...e, { employee_id: '', employee_name: '', hours: '', cost_code_no: '', cost_code_desc: '', notes: '', _added: true }])
  }
  const removeWorker = (i) => setEntries(e => e.filter((_, idx) => idx !== i))
  const updateWorker = (i, k, v) => setEntries(e => e.map((w, idx) => idx === i ? { ...w, [k]: v } : w))

  const addConcrete = () => setConcrete(c => [...c, { cost_code_no: '', cost_code_desc: '', quantity: '', notes: '' }])
  const removeConcrete = (i) => setConcrete(c => c.filter((_, idx) => idx !== i))
  const updateConcrete = (i, k, v) => setConcrete(c => c.map((r, idx) => idx === i ? { ...r, [k]: v } : r))

  const addMasonry = () => setMasonry(m => [...m, { masonry_type: '', quantity: '' }])
  const removeMasonry = (i) => setMasonry(m => m.filter((_, idx) => idx !== i))
  const updateMasonry = (i, k, v) => setMasonry(m => m.map((r, idx) => idx === i ? { ...r, [k]: v } : r))

  const handleSave = async () => {
    setSaving(true)
    const formData = { ...data, weather_temp: weather.temp, weather_desc: weather.desc, weather_precip: weather.precip, weather_humidity: weather.humidity, weather_wind: weather.wind }
    await onSave(form.id, formData, entries, concrete, masonry, userName)
    setSaving(false)
  }

  const handleSubmit = async () => {
    // Validate required fields
    const errors = []
    if (!data.work_description?.trim()) errors.push('Description of work completed is required')
    if (data.non_contract_work === null || data.non_contract_work === undefined) errors.push('Non-contract additional work question must be answered')
    if (data.safety_issue === null || data.safety_issue === undefined) errors.push('Safety issue question must be answered')
    if (data.delivery_no_receipt === null || data.delivery_no_receipt === undefined) errors.push('Deliveries without receipt question must be answered')
    // Cost code required if hours > 0
    const missingCostCode = entries.filter(e => parseFloat(e.hours) > 0 && !e.cost_code_no)
    if (missingCostCode.length > 0) {
      errors.push('Cost code is required for: ' + missingCostCode.map(e => e.employee_name || 'worker').join(', '))
    }
    if (errors.length > 0) {
      alert('Please complete the following required fields before submitting:\n\n• ' + errors.join('\n• '))
      return
    }
    await handleSave()
    await onSubmit(form.id)
    onClose()
  }

  const inp = { borderRadius: 8, border: '0.5px solid var(--border2)', padding: '7px 10px', fontSize: 13, background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }
  const ta = { ...inp, resize: 'vertical', minHeight: 60 }
  const lbl = { fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 5, display: 'block' }
  const sec = { marginBottom: 20, paddingBottom: 16, borderBottom: '0.5px solid var(--color-border-tertiary)' }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 4px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 3 }}>Reference: {form.reference_no}</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111', marginBottom: 3 }}>{fmtDate(form.date)}</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#333', marginBottom: 6 }}>Job: {form.job_name}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {form.submitted_at && (
              <div style={{ fontSize: 11, color: '#888' }}>
                Submitted by <strong>{form.last_saved_by || form.foreman_name}</strong> on {new Date(form.submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
            {form.reviewed_at && (
              <div style={{ fontSize: 11, color: '#888' }}>
                Reviewed by <strong>{form.reviewed_by}</strong> on {new Date(form.reviewed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
            {form.approved_at && (
              <div style={{ fontSize: 11, color: '#1D9E75' }}>
                Approved by <strong>{form.approved_by}</strong> on {new Date(form.approved_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
            {form.updated_at && !form.submitted_at && (
              <div style={{ fontSize: 11, color: '#888' }}>
                Last saved {new Date(form.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
        {/* Weather */}
        {weather.temp && (
          <div style={{ textAlign: 'right', minWidth: 140 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#D85A30' }}>{weather.temp}°</div>
            <div style={{ fontSize: 12, fontWeight: 500 }}>{weather.desc}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{weather.precip} &nbsp;{weather.humidity} &nbsp;{weather.wind}</div>
          </div>
        )}
      </div>

      {/* 1. Work description */}
      <div style={sec}>
        <label style={lbl}>Description of work completed and where? <span style={{ color: '#E24B4A' }}>*</span></label>
        <textarea style={ta} value={data.work_description} onChange={e => set('work_description', e.target.value)} disabled={!canEdit} />
      </div>

      {/* 2. Manpower */}
      <div style={sec}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <label style={{ ...lbl, marginBottom: 0 }}>Manpower</label>
          {canEdit && <button onClick={addWorker} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 7, border: '0.5px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-primary)' }}>+ Add worker</button>}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--color-background-secondary)' }}>
              {['Employee', 'Hours', 'Cost Code', 'Notes', canEdit ? '' : null].filter(Boolean).map(h => (
                <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 500, color: '#555', fontSize: 11, borderBottom: '0.5px solid var(--color-border-tertiary)', textTransform: 'uppercase', letterSpacing: '.3px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map((w, i) => {
              const hasHours = parseFloat(w.hours) > 0
              const isAdded = w._added === true
              return (
              <tr key={i} style={{ borderBottom: '0.5px solid var(--color-border-tertiary)' }}>
                <td style={{ padding: '4px 6px' }}>
                  {canEdit ? (
                    <select style={{ ...inp, fontSize: 12 }} value={w.employee_id}
                      onChange={e => {
                        const emp = roster.find(r => String(r.id) === e.target.value)
                        updateWorker(i, 'employee_id', e.target.value)
                        updateWorker(i, 'employee_name', emp ? emp.name : e.target.value)
                      }}>
                      <option value="">Select worker...</option>
                      {roster.map(r => <option key={r.id} value={r.id}>{r.name} ({String(r.id).padStart(4,'0')})</option>)}
                    </select>
                  ) : <span>{w.employee_name}</span>}
                </td>
                <td style={{ padding: '4px 6px', width: 70 }}>
                  {canEdit ? <input type="number" style={{ ...inp, fontSize: 12 }} value={w.hours} min={0} max={24} step={0.5} onChange={e => updateWorker(i, 'hours', e.target.value)} placeholder="0" /> : <span>{w.hours}</span>}
                </td>
                <td style={{ padding: '4px 6px', width: 200 }}>
                  {hasHours ? (
                    canEdit ? (
                      <select style={{ ...inp, fontSize: 12 }} value={w.cost_code_no}
                        onChange={e => {
                          const cc = costCodes.find(c => String(c.cost_code_no) === e.target.value)
                          updateWorker(i, 'cost_code_no', e.target.value)
                          updateWorker(i, 'cost_code_desc', cc ? cc.description : '')
                        }}>
                        <option value="">Select code...</option>
                        {costCodes.map(cc => <option key={cc.cost_code_no} value={cc.cost_code_no}>{cc.description} ({cc.cost_code_no})</option>)}
                      </select>
                    ) : <span>{w.cost_code_desc} ({w.cost_code_no})</span>
                  ) : <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>—</span>}
                </td>
                <td style={{ padding: '4px 6px' }}>
                  {canEdit ? <input style={{ ...inp, fontSize: 12 }} value={w.notes} onChange={e => updateWorker(i, 'notes', e.target.value)} placeholder={!hasHours ? 'reason for no hours...' : 'notes...'} /> : <span>{w.notes}</span>}
                </td>
                {canEdit && <td style={{ padding: '4px 6px', width: 30 }}>
                  {isAdded
                    ? <button onClick={() => removeWorker(i)} style={{ background: 'none', border: 'none', color: '#E24B4A', fontSize: 16, cursor: 'pointer' }}>×</button>
                    : <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', padding: '0 4px' }} title="Scheduled crew cannot be removed">🔒</span>
                  }
                </td>}
              </tr>
              )
            })}
            {entries.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 12, textAlign: 'center', color: '#aaa', fontSize: 12, fontStyle: 'italic' }}>No workers added yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 3. Production numbers - moved above delays */}
      <div style={sec}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <label style={{ ...lbl, color: '#D85A30', fontSize: 13, marginBottom: 0 }}>Production Numbers — What was completed today</label>
          {canEdit && <button onClick={addConcrete} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '0.5px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer' }}>+ Add row</button>}
        </div>
        {concrete.map((row, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr auto', gap: 8, marginBottom: 6 }}>
            <select style={{ ...inp, fontSize: 12 }} value={row.cost_code_no} onChange={e => { const cc = costCodes.find(c => String(c.cost_code_no) === e.target.value); updateConcrete(i, 'cost_code_no', e.target.value); updateConcrete(i, 'cost_code_desc', cc?.description || '') }} disabled={!canEdit}>
              <option value="">Cost Code...</option>
              {costCodes.map(cc => <option key={cc.cost_code_no} value={cc.cost_code_no}>{cc.description} ({cc.cost_code_no})</option>)}
            </select>
            <input type="number" style={{ ...inp, fontSize: 12 }} placeholder="Qty" value={row.quantity} onChange={e => updateConcrete(i, 'quantity', e.target.value)} disabled={!canEdit} />
            <input style={{ ...inp, fontSize: 12 }} placeholder="Notes" value={row.notes} onChange={e => updateConcrete(i, 'notes', e.target.value)} disabled={!canEdit} />
            {canEdit && <button onClick={() => removeConcrete(i)} style={{ background: 'none', border: 'none', color: '#E24B4A', fontSize: 16, cursor: 'pointer' }}>×</button>}
          </div>
        ))}
      </div>

      {/* 4. Delays */}
      <div style={sec}>
        <label style={lbl}>Delays?</label>
        <textarea style={ta} value={data.delays} onChange={e => set('delays', e.target.value)} disabled={!canEdit} placeholder="Describe any delays..." />
      </div>

      {/* 4. Non-contract work */}
      <div style={{ ...sec, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={lbl}>Non-contract additional work performed? <span style={{ color: '#E24B4A' }}>*</span></label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {['Yes', 'No'].map(v => (
              <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: canEdit ? 'pointer' : 'default', fontSize: 13 }}>
                <input type="radio" name="non_contract" checked={data.non_contract_work === (v === 'Yes')} onChange={() => canEdit && set('non_contract_work', v === 'Yes')} disabled={!canEdit} />
                {v}
              </label>
            ))}
            {data.non_contract_work === null && <div style={{ fontSize: 11, color: '#E24B4A' }}>Required</div>}
          </div>
        </div>
        <div>
          <label style={lbl}>Additional work performed</label>
          <textarea style={{ ...ta, minHeight: 70 }} value={data.non_contract_details} onChange={e => set('non_contract_details', e.target.value)} disabled={!canEdit || !data.non_contract_work} placeholder="Only answer if above is yes" />
        </div>
      </div>

      {/* 5. Safety */}
      <div style={{ ...sec, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={lbl}>Safety issue? <span style={{ color: '#E24B4A' }}>*</span></label>
          {['Yes', 'No'].map(v => (
            <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: canEdit ? 'pointer' : 'default', fontSize: 13, marginBottom: 8 }}>
              <input type="radio" name="safety" checked={data.safety_issue === (v === 'Yes')} onChange={() => canEdit && set('safety_issue', v === 'Yes')} disabled={!canEdit} />
              {v}
            </label>
          ))}
          {data.safety_issue === null && <div style={{ fontSize: 11, color: '#E24B4A' }}>Required</div>}
        </div>
        <div>
          <label style={lbl}>If yes to safety issues what are they?</label>
          <textarea style={{ ...ta, minHeight: 70 }} value={data.safety_details} onChange={e => set('safety_details', e.target.value)} disabled={!canEdit || !data.safety_issue} placeholder="Only answer if yes to safety issues" />
        </div>
      </div>

      {/* 6. Deliveries */}
      <div style={sec}>
        <label style={lbl}>Were there any deliveries to the site without delivery receipt (equipment, dumpsters)? <span style={{ color: '#E24B4A' }}>*</span></label>
        {['Yes', 'No'].map(v => (
          <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: canEdit ? 'pointer' : 'default', fontSize: 13, marginBottom: 8 }}>
            <input type="radio" name="delivery" checked={data.delivery_no_receipt === (v === 'Yes')} onChange={() => canEdit && set('delivery_no_receipt', v === 'Yes')} disabled={!canEdit} />
            {v}
          </label>
        ))}
        {data.delivery_no_receipt === null && <div style={{ fontSize: 11, color: '#E24B4A' }}>Required</div>}
      </div>

      {/* 7. Equipment */}
      <div style={sec}>
        <label style={lbl}>Equipment breakdowns / issues</label>
        <textarea style={ta} value={data.equipment_issues} onChange={e => set('equipment_issues', e.target.value)} disabled={!canEdit} placeholder="Only fill out if any issues or breakdowns" />
      </div>



      {/* 9. Plan for tomorrow */}
      <div style={{ ...sec, background: 'var(--color-background-secondary)', borderRadius: 10, padding: 16, border: 'none' }}>
        <label style={{ ...lbl, color: '#D85A30', fontSize: 13 }}>Plan for tomorrow:</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
          <div>
            <label style={lbl}>Jobsite</label>
            <select style={{ ...inp, fontSize: 12 }} value={data.tomorrow_jobsite} onChange={e => set('tomorrow_jobsite', e.target.value)} disabled={!canEdit}>
              <option value="">Select job...</option>
              {activeJobs.map(j => <option key={j.id} value={j.num + ' ' + j.name}>{j.num} – {j.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Number of men including foreman</label>
            <input type="number" style={{ ...inp, fontSize: 12 }} value={data.tomorrow_men} min={0} onChange={e => set('tomorrow_men', e.target.value)} disabled={!canEdit} />
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={lbl}>Tasks to be performed and goals</label>
          <textarea style={ta} value={data.tomorrow_tasks} onChange={e => set('tomorrow_tasks', e.target.value)} disabled={!canEdit} />
        </div>
        <div>
          <label style={lbl}>Material, equipment and inspections needed in coming days</label>
          <textarea style={ta} value={data.tomorrow_materials} onChange={e => set('tomorrow_materials', e.target.value)} disabled={!canEdit} />
        </div>
      </div>

      {/* Actions */}
      {canEdit && (
        <div style={{ display: 'flex', gap: 10, marginTop: 8, paddingTop: 16, borderTop: '0.5px solid var(--color-border-tertiary)' }}>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '9px 20px', borderRadius: 9, border: '0.5px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
          {form.status === 'draft' && (
            <button onClick={handleSubmit} disabled={saving}
              style={{ padding: '9px 20px', borderRadius: 9, border: 'none', background: '#378ADD', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Submit report
            </button>
          )}
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 9, border: '0.5px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
        </div>
      )}
    </div>
  )
}

// ── Payroll export panel ───────────────────────────────────────────────────────
function PayrollExport() {
  const [weekStart, setWeekStart] = useState('')
  const [weekEnd, setWeekEnd] = useState('')
  const [loading, setLoading] = useState(false)

  // Default to current week Mon-Sun
  useEffect(() => {
    const today = new Date()
    const day = today.getDay()
    const mon = new Date(today); mon.setDate(today.getDate() - (day === 0 ? 6 : day - 1))
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6)
    setWeekStart(mon.toISOString().slice(0, 10))
    setWeekEnd(sun.toISOString().slice(0, 10))
  }, [])

  const handleExport = async () => {
    setLoading(true)
    const rows = await loadHoursForWeek(weekStart, weekEnd)
    if (!rows.length) { alert('No approved hours found for this period'); setLoading(false); return }
    const csv = exportPayrollCSV(rows)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `payroll_${weekStart}_to_${weekEnd}.csv`
    a.click()
    setLoading(false)
  }

  const inp = { borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', padding: '7px 10px', fontSize: 13, background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none' }

  return (
    <div style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 12, padding: 16, maxWidth: 500 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Payroll CSV Export</div>
      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 14 }}>
        Exports approved hours in Foundation payroll import format: employee number, job no, cost code, date, hours
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 3 }}>Week start</div>
          <input type="date" style={inp} value={weekStart} onChange={e => setWeekStart(e.target.value)} />
        </div>
        <div style={{ paddingTop: 16 }}>to</div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 3 }}>Week end</div>
          <input type="date" style={inp} value={weekEnd} onChange={e => setWeekEnd(e.target.value)} />
        </div>
      </div>
      <button onClick={handleExport} disabled={loading || !weekStart || !weekEnd}
        style={{ padding: '9px 20px', borderRadius: 9, border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
        {loading ? 'Generating...' : '⬇ Download CSV'}
      </button>
    </div>
  )
}

// ── Main Field Forms Panel ─────────────────────────────────────────────────────
export default function FieldForms({ role, activeJobs, cellData, roster, userEmail, userName, userId, deepLinkFormId, onClearDeepLink }) {
  const [view, setView] = useState('list') // 'list' | 'form' | 'payroll'
  const [forms, setForms] = useState([])
  const [openForm, setOpenForm] = useState(null)
  const [costCodes, setCostCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [reviewNotes, setReviewNotes] = useState('')
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [foremanFilter, setForemanFilter] = useState('')
  const [jobFilter, setJobFilter] = useState('')
  const [sortBy, setSortBy] = useState('date_desc')
  const [showFilters, setShowFilters] = useState(false)

  const canApprove = role === 'owner' || role === 'road_super' || role === 'pm'
  const canManage = role === 'owner' || role === 'road_super'
  const isForeman = role === 'foreman'

  const fetchForms = useCallback(async () => {
    setLoading(true)
    const filters = isForeman ? { foremanEmail: userEmail, foremanName: userName } : {}
    const data = await loadFieldForms(filters)
    setForms(data)
    setLoading(false)
    return data
  }, [isForeman, userEmail])

  useEffect(() => {
    fetchForms().then(async (data) => {
      // Check for ?form=ID in URL or deepLinkFormId prop and auto-open that form
      const params = new URLSearchParams(window.location.search)
      const formId = deepLinkFormId || params.get('form')
      if (formId && data) {
        const target = data.find(f => String(f.id) === formId)
        if (target) {
          const full = await loadFieldForm(target.id)
          const codes = await loadCostCodesForJob(target.job_id)
          setCostCodes(codes)
          setOpenForm(full)
          setView('form')
          // Clean URL without reload
          window.history.replaceState({}, '', window.location.pathname)
          if (onClearDeepLink) onClearDeepLink()
        }
      }
    })
  }, [fetchForms])

  const openFormDetail = async (form) => {
    const full = await loadFieldForm(form.id)
    const codes = await loadCostCodesForJob(form.job_id)
    setCostCodes(codes)
    setOpenForm(full)
    setView('form')
  }

  const handleSave = async (formId, formData, entries, concreteRows, masonryRows, uName) => {
    await saveFieldForm(formId, formData, entries, concreteRows, masonryRows, userId, uName || userName)
    fetchForms()
  }

  const handleSubmit = async (formId) => {
    await submitFieldForm(formId, userName)
    // Get form details for notification
    const form = forms.find(f => f.id === formId) || openForm
    if (form) {
      const users = await loadAllUsers()
      const managers = (users || []).filter(u => ['owner','road_super','pm'].includes(u.role)).map(u => u.email).filter(Boolean)
      await sendFormNotification('form_submitted', {
        formId, referenceNo: form.reference_no, foremanName: userName,
        foremanEmail: form.foreman_email, jobName: form.job_name,
        date: form.date, recipients: managers
      })
    }
    fetchForms()
  }

  const handleDeleteForm = async (formId) => {
    const { supabase } = await import('../lib/supabase')
    await supabase.from('hours_ledger').delete().eq('form_id', formId)
    await supabase.from('form_entries').delete().eq('form_id', formId)
    await supabase.from('form_concrete').delete().eq('form_id', formId)
    await supabase.from('form_masonry').delete().eq('form_id', formId)
    await supabase.from('field_forms').delete().eq('id', formId)
    setView('list')
    fetchForms()
  }

  const handleApprove = async (formId) => {
    await approveFieldForm(formId, userName)
    // Notify road supers, owners, and payroll manager
    const form = forms.find(f => f.id === formId) || openForm
    if (form) {
      const users = await loadAllUsers()
      const notify = (users || []).filter(u => ['owner','road_super'].includes(u.role)).map(u => u.email).filter(Boolean)
      // Add payroll manager email
      const PAYROLL_EMAIL = 'PaulaColonnello@cornerstonecontracting.net'
      if (!notify.includes(PAYROLL_EMAIL)) notify.push(PAYROLL_EMAIL)
      await sendFormNotification('form_approved', {
        formId, referenceNo: form.reference_no, foremanName: form.foreman_name,
        jobName: form.job_name, date: form.date, recipients: notify
      })
    }
    fetchForms()
    setView('list')
  }

  const handleReview = async (formId) => {
    await updateFormStatus(formId, 'reviewed', userName, reviewNotes)
    fetchForms()
    setView('list')
  }

  // Get unique foremen and jobs for filter dropdowns
  const uniqueForemen = [...new Set(forms.map(f => f.foreman_name).filter(Boolean))].sort()
  const uniqueJobs = [...new Set(forms.map(f => f.job_name).filter(Boolean))].sort()

  const filteredForms = forms
    .filter(f => filter === 'all' || f.status === filter)
    .filter(f => {
      if (search) {
        const s = search.toLowerCase()
        return f.job_name?.toLowerCase().includes(s) ||
               f.foreman_name?.toLowerCase().includes(s) ||
               f.reference_no?.toLowerCase().includes(s)
      }
      return true
    })
    .filter(f => !foremanFilter || f.foreman_name === foremanFilter)
    .filter(f => !jobFilter || f.job_name === jobFilter)
    .filter(f => {
      if (dateFrom) {
        const parts = dateFrom.split('-')
        const from = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]))
        const fParts = (f.date||'').split('-')
        const fd = new Date(parseInt(fParts[0]), parseInt(fParts[1])-1, parseInt(fParts[2]))
        if (fd < from) return false
      }
      if (dateTo) {
        const parts = dateTo.split('-')
        const to = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]))
        const fParts = (f.date||'').split('-')
        const fd = new Date(parseInt(fParts[0]), parseInt(fParts[1])-1, parseInt(fParts[2]))
        if (fd > to) return false
      }
      return true
    })
    .sort((a, b) => {
      if (sortBy === 'date_desc') return (b.date||'').localeCompare(a.date||'')
      if (sortBy === 'date_asc') return (a.date||'').localeCompare(b.date||'')
      if (sortBy === 'foreman') return (a.foreman_name||'').localeCompare(b.foreman_name||'')
      if (sortBy === 'job') return (a.job_name||'').localeCompare(b.job_name||'')
      if (sortBy === 'ref') return parseInt(a.reference_no||0) - parseInt(b.reference_no||0)
      return 0
    })

  const activeFilterCount = [search, foremanFilter, jobFilter, dateFrom, dateTo].filter(Boolean).length

  if (view === 'form' && openForm) {
    // Foreman can edit draft forms only
    // PM can edit draft and submitted forms only (not approved)
    // Road Super and Owner can edit draft, submitted, and approved forms
    const isLocked = openForm.status === 'approved' && openForm.locked !== false
    const canEdit = !isLocked && (
      (openForm.status === 'draft' && (role === 'foreman' || role === 'road_super' || role === 'owner' || role === 'pm')) ||
      (openForm.status === 'submitted' && (role === 'pm' || role === 'road_super' || role === 'owner')) ||
      (openForm.status === 'approved' && (role === 'road_super' || role === 'owner')) ||
      (openForm.status === 'reviewed' && (role === 'pm' || role === 'road_super' || role === 'owner'))
    )
    // PM can approve if their email matches the job's pm_email from Foundation
    const isPmForThisJob = role === 'pm' && (() => {
      const job = activeJobs.find(j => String(j.id) === String(openForm.job_id) || (j.num + ' - ' + j.name) === openForm.job_name)
      if (!job) return true // if we can't find job, allow PM to approve
      if (!job.pmEmail) return true // no PM email set, allow all PMs
      return job.pmEmail.toLowerCase() === userEmail?.toLowerCase()
    })()
    const canApproveForm = role === 'owner' || role === 'road_super' || isPmForThisJob
    const canDelete = role === 'owner' || role === 'road_super'
    const canLock = role === 'owner' || role === 'road_super'
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: 13 }}>← Back</button>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Daily Foreman Field Report</div>
          <StatusPill status={openForm.status} />
        </div>

        <FormEditor
          form={openForm} costCodes={costCodes} roster={roster} activeJobs={activeJobs}
          canEdit={canEdit} cellData={cellData} userName={userName}
          onSave={handleSave} onSubmit={handleSubmit} onClose={() => setView('list')}
        />

        {/* Review/Approve actions for managers */}
        {canApproveForm && (openForm.status === 'submitted' || openForm.status === 'reviewed') && (
          <div style={{ marginTop: 16, padding: 14, background: 'var(--color-background-secondary)', borderRadius: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Review this report</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
              You can edit the form above before approving if corrections are needed.
            </div>
            <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Add reviewer notes (optional)..."
              style={{ width: '100%', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', padding: '7px 10px', fontSize: 13, marginBottom: 10, resize: 'vertical', minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleApprove(openForm.id)}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                ✓ Approve
              </button>
              <button onClick={() => handleReview(openForm.id)}
                style={{ padding: '8px 18px', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>
                Mark reviewed
              </button>
            </div>
          </div>
        )}
        {/* Lock/Unlock for approved forms */}
        {canLock && openForm.status === 'approved' && (
          <div style={{ marginTop: 16, padding: 14, background: 'var(--color-background-secondary)', borderRadius: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>
                {isLocked ? '🔒 Form is locked' : '🔓 Form is unlocked'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                {isLocked ? 'Unlock to allow editing of this approved form.' : 'Form is unlocked and can be edited. Lock it when done.'}
              </div>
            </div>
            <button onClick={async () => {
              const { supabase } = await import('../lib/supabase')
              await supabase.from('field_forms').update({ locked: isLocked ? false : true }).eq('id', openForm.id)
              setOpenForm(f => ({ ...f, locked: isLocked ? false : true }))
            }}
              style={{ padding: '8px 16px', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>
              {isLocked ? '🔓 Unlock' : '🔒 Lock'}
            </button>
          </div>
        )}

        {/* Delete button for road_super and owner on any status */}
        {canDelete && (
          <div style={{ marginTop: 16, padding: 14, background: 'var(--color-background-secondary)', borderRadius: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--color-text-tertiary)' }}>
              {openForm.status === 'approved'
                ? 'This form is approved. Delete to remove hours from the payroll ledger.'
                : 'You can delete this form if it was created in error.'}
            </div>
            <button onClick={() => {
              const msg = openForm.status === 'approved'
                ? 'Delete this approved form? This will also remove the hours from the payroll ledger.'
                : 'Delete this form? This cannot be undone.'
              if (window.confirm(msg)) handleDeleteForm(openForm.id)
            }}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#E24B4A', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
              Delete form
            </button>
          </div>
        )}
      </div>
    )
  }

  if (view === 'payroll') {
    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: 13 }}>← Back</button>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Payroll Export</div>
        </div>
        <PayrollExport />
      </div>
    )
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>Field Reports</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(role === 'owner' || role === 'road_super') && (
            <button onClick={() => setView('payroll')}
              style={{ padding: '6px 14px', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 12 }}>
              ⬇ Payroll export
            </button>
          )}
        </div>
      </div>

      {/* Search bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by job, foreman, or ref #..."
          style={{ flex: 1, borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', padding: '7px 10px', fontSize: 13, background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none' }}
        />
        <button onClick={() => setShowFilters(f => !f)}
          style={{ padding: '7px 12px', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', background: showFilters ? 'var(--color-text-primary)' : 'transparent', color: showFilters ? 'var(--color-background-primary)' : 'var(--color-text-primary)', cursor: 'pointer', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>
          Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
        </button>
        {(search || activeFilterCount > 0) && (
          <button onClick={() => { setSearch(''); setForemanFilter(''); setJobFilter(''); setDateFrom(''); setDateTo('') }}
            style={{ padding: '7px 10px', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Clear
          </button>
        )}
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div style={{ background: 'var(--color-background-secondary)', borderRadius: 10, padding: 12, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.3px' }}>Foreman</div>
              <select value={foremanFilter} onChange={e => setForemanFilter(e.target.value)}
                style={{ width: '100%', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', padding: '6px 8px', fontSize: 12, background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none' }}>
                <option value="">All foremen</option>
                {uniqueForemen.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.3px' }}>Job</div>
              <select value={jobFilter} onChange={e => setJobFilter(e.target.value)}
                style={{ width: '100%', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', padding: '6px 8px', fontSize: 12, background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none' }}>
                <option value="">All jobs</option>
                {uniqueJobs.map(j => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.3px' }}>Date from</div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ width: '100%', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', padding: '6px 8px', fontSize: 12, background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.3px' }}>Date to</div>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ width: '100%', borderRadius: 8, border: '0.5px solid var(--color-border-secondary)', padding: '6px 8px', fontSize: 12, background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.3px' }}>Sort by</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { val: 'date_desc', label: 'Newest first' },
                { val: 'date_asc', label: 'Oldest first' },
                { val: 'foreman', label: 'Foreman' },
                { val: 'job', label: 'Job' },
                { val: 'ref', label: 'Ref #' },
              ].map(s => (
                <button key={s.val} onClick={() => setSortBy(s.val)}
                  style={{ padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: sortBy === s.val ? 600 : 400,
                    background: sortBy === s.val ? 'var(--color-text-primary)' : 'var(--color-background-primary)',
                    color: sortBy === s.val ? 'var(--color-background-primary)' : 'var(--color-text-secondary)' }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Status filter tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
        {['all', 'draft', 'submitted', 'reviewed', 'approved'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: filter === f ? 600 : 400,
              background: filter === f ? 'var(--color-text-primary)' : 'var(--color-background-secondary)',
              color: filter === f ? 'var(--color-background-primary)' : 'var(--color-text-secondary)' }}>
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== 'all' && <span style={{ marginLeft: 4, opacity: .7 }}>({forms.filter(x => x.status === f).length})</span>}
          </button>
        ))}
      </div>

      {/* Results count */}
      {(search || activeFilterCount > 0) && (
        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 8 }}>
          Showing {filteredForms.length} of {forms.length} forms
        </div>
      )}

      {/* Forms list */}
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 13 }}>Loading forms...</div>
      ) : filteredForms.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 13 }}>
          No {filter !== 'all' ? filter : ''} forms yet.
          {filter === 'all' && ' Forms are created automatically when you publish the schedule.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredForms.map(form => (
            <div key={form.id} onClick={() => openFormDetail(form)}
              style={{ background: 'var(--color-background-primary)', border: '0.5px solid var(--color-border-tertiary)', borderLeft: `3px solid ${form.status === 'approved' ? '#1D9E75' : form.status === 'submitted' ? '#EF9F27' : form.status === 'reviewed' ? '#378ADD' : '#ccc'}`, borderRadius: 10, padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  #{form.reference_no} — {form.job_name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                  {form.foreman_name} · {(() => { const p = (form.date||'').split('-'); return p.length===3 ? new Date(parseInt(p[0]), parseInt(p[1])-1, parseInt(p[2])).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : form.date })()}
                </div>
              </div>
              <StatusPill status={form.status} />
              {form.status === 'submitted' && canManage && (
                <span style={{ fontSize: 10, background: '#FAEEDA', color: '#854F0B', borderRadius: 5, padding: '2px 7px', fontWeight: 600 }}>Needs approval</span>
              )}
              {form.status === 'submitted' && !canManage && canApprove && (
                <span style={{ fontSize: 10, background: '#E6F1FB', color: '#0C447C', borderRadius: 5, padding: '2px 7px', fontWeight: 600 }}>Review only</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
